<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Stany materiałów</span>
        <h3 class="page-title">Lista stanów materiałów - produkty</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          @click.prevent="openImportPopup"
          theme="primary"
          class="mr-2">
          <i class="material-icons">
            cloud_upload
          </i>
          Importuj
        </d-button>

        <d-button
          theme="secondary"
          class="ml-2"
          :disabled="exportInProgress"
          @click.prevent="openExportPopup">
          <i
            v-if="!exportInProgress"
            class="material-icons">
            cloud_download
          </i>
          <div
            v-if="exportInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          Eksportuj
        </d-button>
      </d-col>
    </d-row>

    <d-row
      v-if="loaded"
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="materials-stock-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          placeholder="Podaj nazwę produktu"
          v-model="filters.search" />

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">Nazwa produktu</th>
            <th scope="col" class="border-0">Kod produktu</th>
            <th scope="col" class="border-0">Kod ceny</th>
            <th scope="col" class="border-0">Liczba używanych materiałów</th>
            <th scope="col" class="border-0">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(product, index) of products"
            :key="'product-' + index">
            <td class="align-middle">
              {{ product.name }}
            </td>
            <td class="align-middle">
              {{ product.code }}
            </td>
            <td class="align-middle">
              {{ product.price_code }}
            </td>
            <td class="align-middle">
              {{ product.materials_usages_count }}
            </td>
            <td
              class="align-middle"
              style="width: 100px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/stany-materialow/produkty/edycja/' + product.id)"
                class="mr-2"
                title="Aktualizuj">
                <i class="material-icons">edit</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="products.length === 0">
            <td
              colspan="5"
              class="text-center">
              Brak stanów materiałów produktów do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />

    <d-modal
      v-if="showImportPopup"
      @close="closeImportPopup">
      <d-modal-header>
        <d-modal-title>
          Importuj plik ze stanami materiałów produktów
        </d-modal-title>
      </d-modal-header>
      <d-modal-body>
        <d-form>
          <validation-observer ref="import-form" tag="form">
            <div class="form-group">
              <label for="size">
                Importowany rozmiar:
              </label>

              <validation-provider
                rules="required"
                name="size"
                v-slot="{ errors }">
                <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                  <v-select
                    class="w-100"
                    :options="sizesList"
                    v-model="importSize"
                    placeholder="Wybierz rozmiar"
                    :searchable="true"
                    :clearable="true"
                    :multiple="false"
                    :reduce="option => option.value"
                    label="text">
                  </v-select>
                </div>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group">
              <label for="name">
                Importowany plik:
              </label>

              <validation-provider
                rules="required"
                name="file"
                v-slot="{ errors }">
                <file-input
                  :onChange="setNewFile"
                  id="inputFileImport" />
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
          </validation-observer>
        </d-form>
      </d-modal-body>
      <d-modal-footer>
        <d-button
          class="ml-0"
          theme="danger"
          :disabled="importInProgress"
          @click.prevent="closeImportPopup">
          Anuluj
        </d-button>
        <d-button
          class="ml-auto"
          theme="success"
          :disabled="importInProgress"
          @click.prevent="validateImportForm">
          <div
            v-if="importInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          Importuj
        </d-button>
      </d-modal-footer>
    </d-modal>

    <d-modal
      v-if="showExportPopup"
      @close="closeExportPopup">
      <d-modal-header>
        <d-modal-title>
          Eksportu plik ze stanami materiałów produktów
        </d-modal-title>
      </d-modal-header>
      <d-modal-body>
        <d-form>
          <validation-observer ref="export-form" tag="form">
            <div class="form-group">
              <label for="size">
                Eksportowany rozmiar:
              </label>

              <validation-provider
                rules="required"
                name="size"
                v-slot="{ errors }">
                <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                  <v-select
                    class="w-100"
                    :options="sizesList"
                    v-model="exportSize"
                    placeholder="Wybierz rozmiar"
                    :searchable="true"
                    :clearable="true"
                    :multiple="false"
                    :reduce="option => option.value"
                    label="text">
                  </v-select>
                </div>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
          </validation-observer>
        </d-form>
      </d-modal-body>
      <d-modal-footer>
        <d-button
          class="ml-0"
          theme="danger"
          :disabled="exportInProgress"
          @click.prevent="closeExportPopup">
          Anuluj
        </d-button>
        <d-button
          class="ml-auto"
          theme="success"
          :disabled="exportInProgress"
          @click.prevent="validateExportForm">
          <div
            v-if="exportInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          Eksportuj
        </d-button>
      </d-modal-footer>
    </d-modal>
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import jsonToFormData from 'json-form-data';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import Vue from 'vue';

export default {
  name: 'materials-stock',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    sizesList () {
      return this.sizes.map(size => ({
        text: size.name,
        value: size.id
      }));
    }
  },
  data() {
    return {
      products: [],
      sizes: [],
      loaded: false,
      loadError: false,
      importSize: null,
      exportSize: null,
      filters: {
        search: ''
      },
      exportInProgress: false,
      importFile: null,
      importInProgress: false,
      showImportPopup: false,
      showExportPopup: false,
      loadedSizes: false
    };
  },
  mounted () {
    this.loadFilteredData();
    this.loadAdditionalData();
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/sizes/items',
        outputKey: 'sizes',
        loadedKey: 'loadedSizes',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };

      ListUtils.loadItemsData(this, {
        endpoint: '/materials-stock/products/items?page=' + this.currentPage,
        listField: 'products',
        pagination: true,
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: ''
      };

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['materials-stock-filters-form'], this.debouncedLoadFilteredData);
    },
    openExportPopup () {
      this.showExportPopup = true;
    },
    closeExportPopup () {
      this.exportSize = null;
      this.showExportPopup = false;
    },
    validateExportForm () {
      FormUtils.validate(this.$refs['export-form'], this.export);
    },
    openImportPopup () {
      this.showImportPopup = true;
    },
    closeImportPopup () {
      this.importFile = null;
      this.importSize = null;
      this.showImportPopup = false;
    },
    validateImportForm () {
      FormUtils.validate(this.$refs['import-form'], this.import);
    },
    setNewFile (event) {
      this.importFile = event.target.files[0];
    },
    import () {
      this.importInProgress = true;

      let importData = {
        file: this.importFile,
        size_id: this.importSize
      };

      this.$http.request({
        method: 'POST',
        url: '/materials-stock/products/import',
        headers: { 'Content-Type': undefined },
        transformRequest (data) {
          let dataToSend = jsonToFormData(data);
          return dataToSend;
        },
        data: importData
      }).then((response) => {
        if (response.data.success) {
          Vue.swal({
            title: 'Zaimportowany stany materiałów produktów',
            html: 'Udało się pomyślnie zaimportować stany materiałów produktów',
            type: 'success',
            confirmButtonText: 'OK',
            buttonsStyling: true,
            timer: 2000
          });
        }

        this.closeImportPopup();
        this.clearFilters();
        this.importInProgress = false;
      }).catch(error => {
        console.log(error);
        this.closeImportPopup();

        Vue.swal({
          title: 'Import nie powiódł się',
          html: 'Operacja importu stanów materiałów produktów nie powiodła się. Spróbuj ponownie...',
          type: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
        this.importInProgress = false;
      });
    },
    export () {
      this.exportInProgress = true;

      let exportData = {
        size_id: this.exportSize
      };

      this.$http.post('/materials-stock/products/export', exportData, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'stany-materialow-produkty.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.exportInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'Eksport nie powiódł się',
          html: 'Wyeksportowanie stanów materiałów produktów nie powiodło się. Spróbuj ponownie...',
          type: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
        this.exportInProgress = false;
      });
    }
  }
};
</script>

